import './style.scss';

import { useEffect, useRef } from 'react';

export interface IActionSheetItem {
	key: string;
	label: string;
	onSelect: () => void;
	/**
	 * Why this item cannot run right now. The item still renders and still explains itself:
	 * a control that looks unavailable while saying nothing sends the agent hunting for a fault.
	 */
	hint?: string;
	disabled?: boolean;
	danger?: boolean;
}

interface IActionSheet {
	/** Accessible name of the sheet. */
	label: string;
	items: IActionSheetItem[];
	onClose: () => void;
}

/**
 * A bottom sheet of actions.
 *
 * <p>Bottom-anchored because it is reached by long-press or a kebab on a phone held
 * one-handed — the actions belong under the thumb, not at the top of the screen. The only
 * bottom sheet in the app; {@link MessageActionSheet} and the conversation header's kebab are
 * its two callers.</p>
 */
export const ActionSheet = ({ label, items, onClose }: IActionSheet) => {
	const sheetRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Focus in, so a keyboard user is not stranded behind the sheet.
		sheetRef.current?.focus();

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			// Capture + stopPropagation: the same key closes the surrounding right panel, and a
			// bare handler would let one press dismiss both the sheet and the whole chat.
			event.stopPropagation();
			onClose();
		};

		window.addEventListener('keydown', onKeyDown, true);
		return () => window.removeEventListener('keydown', onKeyDown, true);
	}, [onClose]);

	return (
		<div
			className='sc-sheet-backdrop'
			onClick={onClose}
		>
			<div
				className='sc-sheet'
				ref={sheetRef}
				role='dialog'
				aria-modal='true'
				aria-label={label}
				tabIndex={-1}
				onClick={(event) => event.stopPropagation()}
			>
				{items.map((item) => (
					<button
						key={item.key}
						type='button'
						className={`sc-sheet__action ${item.danger ? 'sc-sheet__action--danger' : ''} ${item.disabled ? 'sc-sheet__action--disabled' : ''}`.trim()}
						aria-disabled={item.disabled || undefined}
						onClick={() => {
							if (item.disabled) return;
							item.onSelect();
						}}
					>
						<span className='sc-sheet__label'>{item.label}</span>
						{item.hint ? <span className='sc-sheet__hint'>{item.hint}</span> : null}
					</button>
				))}

				<button
					type='button'
					className='sc-sheet__action'
					onClick={onClose}
				>
					Cancel
				</button>
			</div>
		</div>
	);
};

interface IMessageActionSheet {
	canReply: boolean;
	canEdit: boolean;
	canDelete: boolean;
	onReply: () => void;
	onEdit: () => void;
	onDelete: () => void;
	onClose: () => void;
}

/**
 * Message actions, as a bottom sheet.
 *
 * <p>Lived beside the support panel until a second conversation kind needed it; its own comment
 * promised the move to `components/utils/` the day a second caller appeared.</p>
 */
const MessageActionSheet = ({ canReply, canEdit, canDelete, onReply, onEdit, onDelete, onClose }: IMessageActionSheet) => {
	const items: IActionSheetItem[] = [];
	// First, and available on the other side's messages too — answering an instruction is the
	// common case, and until this existed a long-press on one did nothing at all.
	if (canReply) items.push({ key: 'reply', label: 'Reply', onSelect: onReply });
	if (canEdit) items.push({ key: 'edit', label: 'Edit message', onSelect: onEdit });
	if (canDelete) items.push({ key: 'delete', label: 'Delete for everyone', onSelect: onDelete, danger: true });

	return (
		<ActionSheet
			label='Message actions'
			items={items}
			onClose={onClose}
		/>
	);
};

export default MessageActionSheet;
